(function() {
  'use strict';

  // 存储所有组件实例
  const milestonesInstances = new Map();

  class MilestonesScrollScale {
    constructor(sectionElement) {
      this.section = sectionElement;
      this.scrollWrapper = sectionElement.querySelector('.of-milestones-scroll-wrapper');
      this.scaleContainer = sectionElement.querySelector('.of-milestones-scale-container');
      this.backgroundElement = sectionElement.querySelector('.of-milestones-background');
      this.contentWrapper = sectionElement.querySelector('.of-milestones-content-wrapper');
      
      if (!this.scrollWrapper || !this.scaleContainer || !this.backgroundElement || !this.contentWrapper) {
        console.warn('of-milestones elements not found');
        return;
      }

      // 获取初始缩放值
      const scaleNumberAttr = this.scaleContainer.getAttribute('rg-of-scale-number');
      if (scaleNumberAttr) {
        const scaleValue = scaleNumberAttr.toString().replace('%', '');
        this.initialScale = parseFloat(scaleValue) / 100;
        
        if (isNaN(this.initialScale) || this.initialScale < 0.6 || this.initialScale > 1) {
          this.initialScale = 0.8;
        }
      } else {
        this.initialScale = 0.8;
      }

      // 当前缩放值
      this.backgroundScale = this.initialScale;
      this.contentScale = this.initialScale;
      this.contentOpacity = 0;
      
      // 缓存上一次的滚动进度，避免重复计算
      this.lastScrollProgress = -1;
      this.lastPhase = -1;
      
      // 视频加载状态
      this.isVideoLoaded = false;
      
      // 初始化完成标志（防止页面刷新时浏览器恢复滚动位置触发的抖动）
      this.isInitialized = false;
      
      // 初始化
      this.init();
    }

    // 缓动函数：让动画更丝滑
    easeOutCubic(t) {
      return 1 - Math.pow(1 - t, 3);
    }

    easeInOutCubic(t) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }
    
    // 更平滑的缓动函数 - 用于背景放大
    easeOutQuad(t) {
      return t * (2 - t);
    }
    
    // 线性缓动 - 最流畅
    linear(t) {
      return t;
    }

    init() {
      // 添加加载状态类
      this.scaleContainer.classList.add('loading');
      
      // 强制设置初始状态（使用 force=true 确保立即应用）
      this.setBackgroundScale(this.initialScale, true);
      this.setContentScale(this.initialScale, true);
      this.setContentOpacity(0, true);
      
      // 检测视频加载状态
      this.checkVideoLoaded();
      
      // 绑定滚动事件
      this.bindScrollEvent();
      
      // 绑定窗口大小改变事件
      this.bindResizeEvent();
      
      // 延迟标记初始化完成，给浏览器恢复滚动位置留出时间
      setTimeout(() => {
        this.isInitialized = true;
      }, 100);
    }

    checkVideoLoaded() {
      const videos = this.backgroundElement.querySelectorAll('video');
      
      if (videos.length === 0) {
        // 没有视频，直接标记为已加载
        this.isVideoLoaded = true;
        this.scaleContainer.classList.remove('loading');
        return;
      }
      
      let loadedCount = 0;
      const totalVideos = videos.length;
      const videoStates = new Array(videos.length).fill(false); // 跟踪每个视频的加载状态
      
      const markAsLoaded = (index) => {
        // 防止同一个视频重复标记
        if (videoStates[index]) {
          return;
        }
        
        videoStates[index] = true;
        loadedCount++;
        
        if (loadedCount >= totalVideos) {
          this.isVideoLoaded = true;
          // 移除加载状态类，启用过渡效果
          this.scaleContainer.classList.remove('loading');
        }
      };
      
      videos.forEach((video, index) => {
        // 检查视频是否已经加载
        if (video.readyState >= 3) {
          // HAVE_FUTURE_DATA 或更高状态
          markAsLoaded(index);
        } else {
          // 只监听一个事件，优先使用 canplay
          video.addEventListener('canplay', () => {
            markAsLoaded(index);
          }, { once: true });
          
          // 超时保护：3秒后强制标记为已加载
          setTimeout(() => {
            if (!videoStates[index]) {
              markAsLoaded(index);
            }
          }, 3000);
        }
      });
    }

    setBackgroundScale(scale, force = false) {
      if (!this.backgroundElement) return;
      const newScale = Math.max(this.initialScale, Math.min(1, scale));
      
      // 降低阈值，让变化更细腻流畅（除非强制更新）
      if (force || Math.abs(this.backgroundScale - newScale) > 0.0001) {
        this.backgroundScale = newScale;
        // 四舍五入到4位小数，更精确
        const roundedScale = Math.round(this.backgroundScale * 10000) / 10000;
        // 使用 translate3d 强制 GPU 加速
        this.backgroundElement.style.transform = `scale(${roundedScale}) translateZ(0)`;
      }
    }

    setContentScale(scale, force = false) {
      if (!this.contentWrapper) return;
      const newScale = Math.max(this.initialScale, Math.min(1, scale));
      
      // 降低阈值，让变化更细腻流畅（除非强制更新）
      if (force || Math.abs(this.contentScale - newScale) > 0.0001) {
        this.contentScale = newScale;
        // 四舍五入到4位小数，更精确
        const roundedScale = Math.round(this.contentScale * 10000) / 10000;
        // 使用 translate3d 强制 GPU 加速
        this.contentWrapper.style.transform = `scale(${roundedScale}) translateZ(0)`;
      }
    }

    setContentOpacity(opacity, force = false) {
      if (!this.contentWrapper) return;
      const newOpacity = Math.max(0, Math.min(1, opacity));
      
      // 降低阈值，让变化更细腻流畅（除非强制更新）
      if (force || Math.abs(this.contentOpacity - newOpacity) > 0.0001) {
        this.contentOpacity = newOpacity;
        // 四舍五入到4位小数，更精确
        const roundedOpacity = Math.round(this.contentOpacity * 10000) / 10000;
        this.contentWrapper.style.opacity = roundedOpacity;
      }
    }

    bindScrollEvent() {
      let ticking = false;
      
      const handleScroll = () => {
        if (!ticking) {
          ticking = true;
          
          // 不使用节流，每次滚动都立即requestAnimationFrame，确保最流畅
          window.requestAnimationFrame(() => {
            this.update();
            ticking = false;
          });
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      this.scrollHandler = handleScroll;
    }

    update() {
      if (!this.scrollWrapper || !this.section) return;
      
      // 如果初始化未完成，跳过更新（防止页面刷新时浏览器恢复滚动位置触发的抖动）
      if (!this.isInitialized) {
        return;
      }
      
      // 如果视频还没加载完成，完全跳过更新（不做任何操作）
      if (!this.isVideoLoaded) {
        return;
      }

      // 获取滚动包装器的位置信息
      const wrapperRect = this.scrollWrapper.getBoundingClientRect();
      const wrapperTop = wrapperRect.top;
      const wrapperHeight = wrapperRect.height; // 350vh
      const windowHeight = window.innerHeight;
      
      // 滚动进度计算 - 基于组件在视口中的位置
      // 关键：我们希望组件在sticky状态下停留更久
      // 当wrapper的top从windowHeight滚动到-wrapperHeight时，整个滚动过程完成
      let scrollProgress = 0;
      
      if (wrapperTop <= windowHeight && wrapperTop >= -wrapperHeight) {
        // wrapper 在可见范围内
        const scrolled = windowHeight - wrapperTop; // 已滚动的距离
        const totalScrollDistance = windowHeight + wrapperHeight; // 总滚动距离 = 100vh + 350vh = 450vh
        scrollProgress = scrolled / totalScrollDistance;
        scrollProgress = Math.max(0, Math.min(1, scrollProgress));
      } else if (wrapperTop < -wrapperHeight) {
        // wrapper 已经完全滚过
        scrollProgress = 1;
      } else {
        // wrapper 还未进入视口
        scrollProgress = 0;
      }
      
      // 定义阶段的进度范围（基于350vh容器，总滚动450vh）
      // 第一阶段：0 - 0.20 背景从初始值放大到100%（快速完成）
      // 等待阶段：0.20 - 0.45 背景保持100%，文字还未出现（空滑动时间更长）
      // 第二阶段：0.45 - 0.60 内容从初始值放大到100%，同时opacity从0到1
      // 停留阶段：0.60 - 1.0 内容保持100%（占整个滚动的40%，相当于180vh的滚动距离）
      
      const phase1Start = 0;
      const phase1End = 0.20;     // 背景放大在20%完成（90vh的滚动距离）
      const phase2Start = 0.45;   // 文字在45%开始出现（202.5vh位置，空滑动时间为112.5vh）
      const phase2End = 0.60;     // 文字在60%完成（270vh位置），之后停留180vh
      
      // 如果进度没有明显变化，跳过更新（防止抖动）
      if (Math.abs(scrollProgress - this.lastScrollProgress) < 0.0001) {
        return;
      }
      this.lastScrollProgress = scrollProgress;
      
      if (scrollProgress <= phase1Start) {
        // 还未开始动画
        this.setBackgroundScale(this.initialScale);
        this.setContentScale(this.initialScale);
        this.setContentOpacity(0);
      } else if (scrollProgress <= phase1End) {
        // 第一阶段：背景放大（使用线性缓动，最流畅）
        const phase1Progress = (scrollProgress - phase1Start) / (phase1End - phase1Start);
        // 使用线性缓动，避免卡顿感
        const easedProgress = this.linear(phase1Progress);
        const backgroundScale = this.initialScale + (1 - this.initialScale) * easedProgress;
        
        this.setBackgroundScale(backgroundScale);
        this.setContentScale(this.initialScale);
        this.setContentOpacity(0);
      } else if (scrollProgress < phase2Start) {
        // 等待阶段：背景已经100%，但文字还未开始显示
        this.setBackgroundScale(1);
        this.setContentScale(this.initialScale);
        this.setContentOpacity(0);
      } else if (scrollProgress <= phase2End) {
        // 第二阶段：内容放大和淡入（使用更平滑的缓动函数）
        const phase2Progress = (scrollProgress - phase2Start) / (phase2End - phase2Start);
        const easedProgress = this.easeOutCubic(phase2Progress);
        const contentScale = this.initialScale + (1 - this.initialScale) * easedProgress;
        const contentOpacity = easedProgress;
        
        this.setBackgroundScale(1);
        this.setContentScale(contentScale);
        this.setContentOpacity(contentOpacity);
      } else {
        // 动画完成
        this.setBackgroundScale(1);
        this.setContentScale(1);
        this.setContentOpacity(1);
      }
    }

    bindResizeEvent() {
      let resizeTimeout;
      const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this.update();
        }, 100);
      };

      window.addEventListener('resize', handleResize);
      this.resizeHandler = handleResize;
    }

    destroy() {
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
      }
      
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }
      
      if (this.backgroundElement) {
        this.backgroundElement.style.transform = '';
        this.backgroundElement.style.transformOrigin = '';
      }
      
      if (this.contentWrapper) {
        this.contentWrapper.style.transform = '';
        this.contentWrapper.style.transformOrigin = '';
        this.contentWrapper.style.opacity = '';
      }
    }
  }

  // 初始化所有 milestones 组件
  function initMilestonesComponents() {
    const sections = document.querySelectorAll('.of-milestones-section');
    
    sections.forEach((section, index) => {
      if (!section.id) {
        section.id = `of-milestones-${index}-${Date.now()}`;
      }
      
      // 如果已经存在实例，先销毁
      if (milestonesInstances.has(section.id)) {
        milestonesInstances.get(section.id).destroy();
      }
      
      const instance = new MilestonesScrollScale(section);
      milestonesInstances.set(section.id, instance);
    });
  }

  // DOM 加载完成后初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMilestonesComponents);
  } else {
    initMilestonesComponents();
  }

  // 监听动态内容加载（使用防抖避免频繁重复初始化）
  if (typeof MutationObserver !== 'undefined') {
    let mutationTimer = null;
    
    const observer = new MutationObserver((mutations) => {
      // 检查是否有新的 milestones 组件被添加
      let hasNewMilestones = false;
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) { // Element node
            if (node.classList && node.classList.contains('of-milestones-section')) {
              hasNewMilestones = true;
            } else if (node.querySelector && node.querySelector('.of-milestones-section')) {
              hasNewMilestones = true;
            }
          }
        });
      });
      
      if (!hasNewMilestones) {
        return;
      }
      
      // 使用防抖，避免短时间内多次初始化
      clearTimeout(mutationTimer);
      mutationTimer = setTimeout(() => {
        initMilestonesComponents();
      }, 100);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // 导出全局方法
  window.refreshMilestonesComponents = initMilestonesComponents;
  window.destroyMilestonesComponents = () => {
    milestonesInstances.forEach(instance => instance.destroy());
    milestonesInstances.clear();
  };
})();
